import React, { useMemo, useState } from "react";
import { ExpandablePanel } from "@ddm-design-system/expandable-panel";
import { MultipleDropdown } from "@ddm-design-system/dropdown";
import { Icon, IconNames } from "@ddm-design-system/icon";
import { Description, DescriptionMedium, SectionTitle } from "@ddm-design-system/typography";
import { ILine } from "../../../store/outlet/types";
import { IX10, X10Level } from "../../../store/installation/types";
import useContent from "../../../hooks/useContent";
import ErrorList from "../ErrorList";
import X10Card from "./X10Card";
import useX10Text from "./useX10Text";
import { formatLitres, formatTemperature } from "./x10Presentation";

interface IProps {
  line: ILine;
  initialExpanded: boolean;
  onSelect: (x10: IX10) => void;
}

const FILTER_LEVELS: { [id: string]: X10Level } = {
  Ok: "ok",
  Warning: "warning",
  Error: "danger",
  Offline: "offline"
};

const SummaryTile: React.FC<{ icon: IconNames; label: string; value: string; sub: string }> = ({
  icon,
  label,
  value,
  sub
}) => (
  <div
    className="flex flex-col gap-xxs flex-1 min-w-[160px] box-border p-md rounded-10 bg-white text-left"
    data-test-id="x10-summary-tile"
  >
    <span className="flex items-center gap-xs text-grey-grey100">
      <Icon name={icon} size={16} className="fill-current" />
      <Description>{label}</Description>
    </span>
    <SectionTitle>{value}</SectionTitle>
    <Description className="text-grey-grey100">{sub}</Description>
  </div>
);

export const X10Line: React.FC<IProps> = ({ line, initialExpanded, onSelect }) => {
  const { managerAppCommon: content } = useContent();
  const t = useX10Text();
  const [errorsFilter, setErrorsFilter] = useState<Array<{ id: string; text: string }>>([]);
  const x10s = useMemo(() => line.x10s || [], [line.x10s]);

  const errorsFilterItems = [
    { id: "Ok", text: content.common_ok },
    { id: "Warning", text: content.common_warning },
    { id: "Error", text: content.common_error },
    { id: "Offline", text: content.common_offline }
  ];

  const filtered = useMemo(() => {
    if (errorsFilter.length === 0) {
      return x10s;
    }
    const levels = errorsFilter.map(f => FILTER_LEVELS[f.id]);
    return x10s.filter(x10 => levels.includes(x10.status));
  }, [x10s, errorsFilter]);

  const summary = useMemo(() => {
    const online = x10s.filter(x10 => !x10.offline);
    const withKeg = online.filter(x10 => x10.hasKeg);
    const temperatures = online
      .filter(x10 => !x10.evaluation.open && x10.vesselTemperature !== undefined)
      .map(x10 => x10.vesselTemperature as number);
    return {
      online: online.length,
      litres: withKeg.reduce((total, x10) => total + x10.volumeLeft, 0),
      kegs: withKeg.length,
      averageTemperature:
        temperatures.length > 0
          ? temperatures.reduce((total, value) => total + value, 0) / temperatures.length
          : undefined,
      kegsToChange: online.filter(
        ({ evaluation }) =>
          evaluation.noKeg ||
          evaluation.empty ||
          evaluation.kegTime === "warning" ||
          evaluation.kegTime === "danger"
      ).length
    };
  }, [x10s]);

  return (
    // @ts-ignore
    <ExpandablePanel
      key={line.index}
      title={t("machines")}
      className="rounded-10 !border-0 md:p-md xs:p-md bg-grey-grey25 mt-lg"
      initialExpanded={initialExpanded}
      renderHeaderDetails={() => <ErrorList errors={line.errors} />}
    >
      <div className="flex flex-col w-full text-left px-lg xs:px-0" data-test-id="x10-line">
        <div className="flex flex-wrap gap-md pb-lg">
          <SummaryTile
            icon="Wifi"
            label={t("online")}
            value={`${summary.online} / ${x10s.length}`}
            sub={t("machines")}
          />
          <SummaryTile
            icon="Keg"
            label={t("beer_on_tap")}
            value={`${formatLitres(summary.litres)} L`}
            sub={t("beer_on_tap_sub", { COUNT: summary.kegs })}
          />
          <SummaryTile
            icon="Temperature"
            label={t("average_temperature")}
            value={formatTemperature(summary.averageTemperature)}
            sub={t("average_temperature_sub")}
          />
          <SummaryTile
            icon="Sleep"
            label={t("kegs_to_change")}
            value={`${summary.kegsToChange}`}
            sub={t("kegs_to_change_sub")}
          />
        </div>
        <div className="flex flex-row justify-between items-center md:flex-col md:items-start pb-lg">
          <DescriptionMedium className="text-grey-grey100">{t("machines")}</DescriptionMedium>
          <div data-test-id="x10-state-filter">
            {/* @ts-ignore */}
            <MultipleDropdown
              label={`${content.common_filters}:`}
              items={errorsFilterItems}
              selectedItems={errorsFilter}
              className="!bg-transparent ddl-components-state"
              onItemsSelected={(items: any) => setErrorsFilter(items)}
              placeholder={content.common_all}
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="flex w-full py-lg justify-center">
            <div>{content.no_components_found}</div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-xxl mb-xxl xs:gap-xs" data-test-id="components-x10s">
            {filtered.map(x10 => (
              <X10Card key={x10.id} x10={x10} onClick={onSelect} />
            ))}
          </div>
        )}
      </div>
    </ExpandablePanel>
  );
};

export default X10Line;
